import { Linking, Pressable, Text, View } from "react-native";

import { APP_BASE_URL } from "@/config";
import { slopToFloor } from "@/lib/ui/touchTarget";

/**
 * The one Trovie affordance on a page that belongs to a vendor.
 *
 * A signed-out viewer needs a way in; a signed-in vendor needs a way back to their own app. This
 * is one control for both, because the page mounts no authenticated shell: `isPublicRoutePath`
 * keeps the vendor app's `ClerkProvider` off these routes, so a session-aware branch here would
 * mean mounting the whole vendor app beneath every anonymous QR scan. One isolated control may
 * read a session, the shop's Follow button, which mounts its own scope and renders nothing until
 * a usable session exists (ADR-0141, amended). This footer does not read it.
 *
 * The app root resolves it instead. Its gate already sends a signed-in vendor to their workspace
 * and a signed-out one to sign-in: the same branch, evaluated where a session exists.
 *
 * Absolute via `APP_BASE_URL` rather than a router push: while `browse.trovie.io` still serves
 * these pages this is a cross-origin hop, and once it does not, the value is this origin anyway.
 *
 * Quiet on purpose. The storefront is the vendor's, and a second prominent action beside their own
 * links would read as Trovie advertising on a page a customer came to for cards.
 */
export function PublicPageFooter() {
  return (
    <View className="w-full flex-row items-center justify-center gap-3 border-t border-border px-4 py-5">
      <Text className="text-xs font-body text-muted-foreground">
        This shop runs on Trovie
      </Text>
      <Pressable
        onPress={() => void Linking.openURL(`${APP_BASE_URL}/`)}
        accessibilityRole="link"
        accessibilityLabel="Open Trovie"
        testID="public-page-footer-open-trovie"
        hitSlop={slopToFloor(44)}
        className="rounded-full border border-border bg-surface px-3 py-1.5 active:opacity-70"
      >
        <Text className="text-xs font-label text-foreground">Open Trovie</Text>
      </Pressable>
    </View>
  );
}
